/**
 * **The small player on a task card** (owner, 25 September 2026): `ClipMiniPlayer`'s `card` variant
 * over the clip the card names — the same read the card's poster made (`clipQueryOptions`, one cache
 * entry, one signed url), the same seen mark on the first frame, the same thread.
 *
 * ── The box stands before the clip's read lands ──────────────────────────────────────────────
 * The film's shape is on the queue payload, so the wait draws the player's own grid — the film's box
 * at its final size and the control row's height under it, with the card's `aside` beside them — and
 * the loaded player takes exactly that room.
 *
 * Under the reply stands the movement's trend (`entities/lift-history`'s `LiftTrendChart`), read
 * once the clip names its movement.
 */

import { useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { clipQueryOptions } from '~/entities/exercise-clip';
import { LiftTrendChart } from '~/entities/lift-history';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import styles from './ClipMiniPlayer.module.css';
import { ClipMiniPlayer } from './ClipMiniPlayer';

export interface ClipCardPlayerProps {
  readonly clientId: number;
  readonly clipId: number;
  /** `width / height` from the queue payload — the box's shape before the read. */
  readonly aspect: number;
  /** The second the film opens at: the client's question, where it is pinned. */
  readonly startAt: number | null;
  /** What the film is, for assistive technology. */
  readonly label: string;
  /** What the card is about, beside the film. */
  readonly aside: ReactNode;
  /** The reply field, which the card's „Zostaw notatkę" writes into. */
  readonly fieldRef: RefObject<HTMLInputElement | null>;
  /** „Analizuj": the full player, at the second the film was left on or at the top. */
  readonly onOpen: (startAt: number | null) => void;
  readonly reducedMotion: boolean;
}

export function ClipCardPlayer({
  clientId,
  clipId,
  aspect,
  startAt,
  label,
  aside,
  fieldRef,
  onOpen,
  reducedMotion,
}: ClipCardPlayerProps) {
  const { t } = useTranslation();
  const clip = useQuery(clipQueryOptions(clipId));
  const gate = useSkeletonGate(clip.isPending);

  if (gate.waiting || clip.data === null || clip.data === undefined) {
    const failed = !gate.waiting && (clip.isError || clip.data === null);
    return (
      <div className={`${String(styles.mini)} ${String(styles.card)}`} aria-busy={gate.waiting}>
        <div
          className={`${String(styles.box)} ${gate.skeleton ? String(skel.bar) : ''}`}
          style={{ '--clip-ratio': String(aspect) } as CSSProperties}
        />
        <div className={styles.row} />
        <div className={styles.side}>
          {aside}
          {failed ? <p className={gate.reveal}>{t('web:tasks.blocks.readFailed')}</p> : null}
        </div>
        {gate.waiting ? <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span> : null}
      </div>
    );
  }

  return (
    <div className={gate.reveal}>
      <ClipMiniPlayer
        clientId={clientId}
        clip={clip.data}
        label={label}
        held={false}
        onOpen={onOpen}
        onActivity={() => undefined}
        variant="card"
        startAt={startAt}
        aside={aside}
        fieldRef={fieldRef}
        // The movement's trend under the reply (owner, 26 September 2026): the same records read
        // the Forma tab draws, for the movement the clip names.
        footer={<LiftTrendChart clientId={clientId} exerciseId={clip.data.exerciseId} reducedMotion={reducedMotion} />}
      />
    </div>
  );
}
